import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Database, Download, RefreshCw, ShieldCheck, FileSpreadsheet, Sigma,
  Link as LinkIcon, FileText, Copy,
} from 'lucide-react';

/**
 * Soul CSV Export · CEO-only tile.
 *
 * Pattern #35 (bridge document) — emits the 247concierge-canonical
 * CSV with the full Soul-Form data for every Primary+Active member
 * of a client. Used by 247concierge's substrate adapter until their
 * webhook is live.
 *
 * Backend:
 *   GET  /api/customer-intelligence/_admin/export-soul-csv/last-export?client_id=jlr
 *   POST /api/customer-intelligence/_admin/export-soul-csv?client_id=jlr&completed_only=…
 *
 * Doctrine:
 *   • ISO YYYY-MM-DD dates · pipe-separated multi-selects
 *   • CEO-only · every export audit-logged with SHA-256
 *   • 58 columns: Layer 1 (13) + Layer 2 (39) + Layer 3 (6)
 */
export default function SoulCsvExportTile({ fixedClientId = null } = {}) {
  const [clientId, setClientId] = useState(fixedClientId || 'jlr');
  const [completedOnly, setCompletedOnly] = useState(false);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchMeta = async (cid = clientId) => {
    setLoading(true);
    try {
      const r = await api.get(
        '/customer-intelligence/_admin/export-soul-csv/last-export',
        { params: { client_id: cid } },
      );
      setMeta(r.data);
    } catch (e) {
      toast.error(`Could not load CSV history: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMeta(); /* eslint-disable-next-line */ }, [clientId]);

  const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      return `${dd}/${mm}/${yy} · ${hh}:${mi}`;
    } catch (e) { return iso; }
  };

  const generate = async () => {
    if (!window.confirm(
      `Generate Soul CSV for ${clientId.toUpperCase()}?\n\n` +
      `This exports every Primary+Active member` +
      (completedOnly ? ' with soul_form_completed=True' : '') +
      `. The CSV will download immediately. ` +
      `Export will be SHA-256 stamped and audit-logged.`,
    )) return;

    setExporting(true);
    try {
      // Stream as blob so we can capture headers (sha256 · row-count) and
      // trigger an in-page download in one motion.
      const r = await api.post(
        '/customer-intelligence/_admin/export-soul-csv',
        null,
        {
          params: { client_id: clientId, completed_only: completedOnly },
          responseType: 'blob',
        },
      );

      const rowCount = r.headers['x-soul-csv-row-count'] || '?';
      const sha256 = r.headers['x-soul-csv-sha256'] || '';
      const cd = r.headers['content-disposition'] || '';
      const fnMatch = cd.match(/filename="([^"]+)"/);
      const filename = fnMatch ? fnMatch[1]
                        : `soul-${clientId}-${new Date().toISOString().slice(0, 10)}.csv`;

      // Force download
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Exported ${rowCount} members · ${filename}`);
      // Refresh metadata to show the new last-export row.
      fetchMeta();
      // Console-log the sha256 in case the user needs it for verification.
      // eslint-disable-next-line no-console
      console.info(`[Soul CSV] sha256 = ${sha256}`);
    } catch (e) {
      // The error path needs special handling — the response is a blob,
      // so the detail string isn't parsed by axios. Read it manually.
      let detail = e?.message || 'export failed';
      if (e?.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          detail = j.detail || txt;
        } catch (parseErr) { /* fallthrough */ }
      } else if (e?.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      toast.error(`Export failed: ${detail}`);
    } finally {
      setExporting(false);
    }
  };

  if (loading && !meta) {
    return (
      <div className="rounded-xl border p-4 text-xs"
        style={{ borderColor: '#E5E1DC', background: '#FAF8F5', color: '#6B7280' }}>
        Loading Soul CSV history…
      </div>
    );
  }

  const last = meta?.last_export;

  return (
    <div className="rounded-xl border p-4"
      style={{ borderColor: '#E5E1DC',
               background: 'linear-gradient(135deg, #FAF8F5 0%, #F0EBE3 100%)' }}
      data-testid="soul-csv-export-tile">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#1B2A4A' }}>
            <Database size={18} style={{ color: '#F5C86B' }} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>
                Soul CSV Export
              </p>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold"
                style={{ backgroundColor: '#1B2A4A', color: '#F5C86B' }}>
                Pattern #35
              </span>
            </div>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              247concierge-canonical · ISO dates · pipe-separated · SHA-256 stamped · audit-logged
            </p>
          </div>
        </div>
        <button onClick={() => fetchMeta()} disabled={loading}
          title="Reload last-export metadata"
          className="px-2 py-1.5 rounded text-[11px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-60"
          style={{ borderColor: '#9CA3AF', color: '#1B2A4A' }}
          data-testid="soul-csv-refresh-btn">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Controls — client + completedOnly */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-md border bg-white p-2.5" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-[10px] uppercase tracking-wider font-semibold mb-1"
             style={{ color: '#6B7280' }}>Client</p>
          {fixedClientId ? (
            <div className="text-[12.5px] font-medium" style={{ color: '#1B2A4A' }}
                  data-testid="soul-csv-client-locked">
              {fixedClientId.toUpperCase()} <span style={{ color: '#9CA3AF' }}>· locked</span>
            </div>
          ) : (
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}
              className="w-full text-[12.5px] font-medium bg-transparent outline-none"
              style={{ color: '#1B2A4A' }}
              data-testid="soul-csv-client-select">
              <option value="jlr">JLR</option>
              <option value="aabe">Aabe Bharat</option>
              <option value="dreamfolks">DreamFolks</option>
              <option value="xiaomi">Xiaomi</option>
              <option value="bhutan">Bhutan</option>
            </select>
          )}
        </div>
        <label className="rounded-md border bg-white p-2.5 cursor-pointer hover:bg-gray-50"
          style={{ borderColor: '#E5E1DC' }}
          data-testid="soul-csv-completed-only-label">
          <p className="text-[10px] uppercase tracking-wider font-semibold mb-1"
             style={{ color: '#6B7280' }}>Filter</p>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={completedOnly}
              onChange={(e) => setCompletedOnly(e.target.checked)}
              data-testid="soul-csv-completed-only-checkbox" />
            <span className="text-[12px] font-medium" style={{ color: '#1B2A4A' }}>
              Only completed souls
            </span>
          </div>
        </label>
      </div>

      {/* Eligible count strip */}
      <div className="rounded-md border bg-white p-3 mb-3 flex items-center justify-between"
        style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center gap-2">
          <Sigma size={14} style={{ color: '#2A7B88' }} />
          <span className="text-[11px]" style={{ color: '#6B7280' }}>
            Would export now:
          </span>
        </div>
        <div className="text-[13px] font-semibold tabular-nums"
             style={{ color: '#1B2A4A' }}
             data-testid="soul-csv-would-export-count">
          {completedOnly
            ? `${meta?.eligible_completed ?? 0} members (completed only)`
            : `${meta?.eligible_total ?? 0} members (all Primary + Active)`}
        </div>
      </div>

      {/* Big generate button */}
      <button onClick={generate} disabled={exporting}
        className="w-full px-4 py-3 rounded-md text-[13px] font-semibold text-white inline-flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-60"
        style={{ backgroundColor: '#1B2A4A' }}
        data-testid="soul-csv-generate-btn">
        {exporting ? (
          <>
            <RefreshCw size={14} className="animate-spin" />
            Generating CSV…
          </>
        ) : (
          <>
            <Download size={14} />
            Generate Soul CSV for 247concierge
          </>
        )}
      </button>

      {/* Last export panel */}
      {last ? (
        <div className="mt-3 rounded-md border p-3 bg-white"
          style={{ borderColor: '#E5E1DC' }}
          data-testid="soul-csv-last-export">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <FileSpreadsheet size={12} style={{ color: '#2A7B88' }} />
                <span className="text-[11px] font-semibold uppercase tracking-wider"
                      style={{ color: '#1B2A4A' }}>
                  Last export
                </span>
              </div>
              <p className="text-[12px]" style={{ color: '#1B2A4A' }}>
                <span className="tabular-nums">{fmtDateTime(last.exported_at)}</span>
                {' · '}
                <span className="font-semibold tabular-nums">
                  {last.row_count} {last.row_count === 1 ? 'row' : 'rows'}
                </span>
                {' · '}
                <span style={{ color: '#6B7280' }}>by {last.exported_by}</span>
              </p>
              <p className="text-[10.5px] mt-1 font-mono break-all"
                 style={{ color: '#6B7280' }}>
                sha256 · {(last.sha256 || '').slice(0, 24)}…
              </p>
              {last.completed_only && (
                <span className="inline-block mt-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
                  style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                  completed only
                </span>
              )}
            </div>
            <ShieldCheck size={14} style={{ color: '#16653A' }} />
          </div>
        </div>
      ) : (
        <div className="mt-3 rounded-md border p-3 text-[11.5px]"
          style={{ borderColor: '#E5E1DC', background: '#FFFBEB', color: '#7A4F08' }}>
          No exports yet for {clientId.toUpperCase()}. Click <strong>Generate</strong> above to produce the first one.
        </div>
      )}

      {/* Doctrine footer */}
      <div className="mt-3 pt-3 border-t text-[10.5px] leading-relaxed"
        style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
        <strong style={{ color: '#1B2A4A' }}>Doctrine:</strong>{' '}
        58 columns · Layer 1 (CRM identity) + Layer 2 (Soul) + Layer 3 (Ops audit).
        Dates as ISO YYYY-MM-DD for transport (humans always see DD/MM/YYYY).
        Multi-selects pipe-separated. Hand the file to 247concierge — they import
        idempotently on <code>membership_id</code>.
      </div>

      {/* Shareable playbook links — issue a fresh public URL, copy & WhatsApp. */}
      <ShareableDocs />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// ShareableDocs — sub-panel for one-click issuance of signed download URLs
// for internal playbooks (Tanisha's Zoho setup, 247concierge spec, etc).
// Lives inside the CSV tile because both pieces serve the same workflow:
// "I just exported the CSV, now I need to send Tanisha her setup doc."
// ─────────────────────────────────────────────────────────────────────────────
function ShareableDocs() {
  const [catalogue, setCatalogue] = useState([]);
  const [issuing, setIssuing] = useState(null);   // doc_id currently issuing
  const [issued, setIssued] = useState({});       // doc_id → {url, expires_at}

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/docs/catalogue');
        setCatalogue(r.data.docs || []);
      } catch (e) {
        // Silent — the rest of the tile still works.
        // eslint-disable-next-line no-console
        console.warn('Docs catalogue unavailable:', e?.message);
      }
    })();
  }, []);

  const issue = async (docId) => {
    setIssuing(docId);
    try {
      const r = await api.post(`/admin/docs/issue-link/${docId}`, null,
        { params: { expires_in_hours: 720 } }); // 30 days
      setIssued({ ...issued, [docId]: r.data });
      toast.success(`Fresh link minted (valid 30 days). Click "Copy" to share.`);
    } catch (e) {
      toast.error(`Could not mint link: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setIssuing(null);
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied. Paste into WhatsApp / email and send.');
    } catch (e) {
      toast.error('Copy failed — long-press the link to copy manually.');
    }
  };

  if (!catalogue.length) return null;

  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: '#E5E1DC' }}
      data-testid="shareable-docs-panel">
      <div className="flex items-center gap-2 mb-2">
        <FileText size={11} style={{ color: '#2A7B88' }} />
        <span className="text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: '#1B2A4A' }}>
          Shareable playbooks
        </span>
        <span className="text-[10px]" style={{ color: '#6B7280' }}>
          · public · token-gated · 30-day expiry
        </span>
      </div>
      <ul className="space-y-2">
        {catalogue.map((doc) => {
          const link = issued[doc.doc_id];
          return (
            <li key={doc.doc_id} className="rounded-md border bg-white p-2.5"
              style={{ borderColor: '#E5E1DC' }}
              data-testid={`shareable-doc-${doc.doc_id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[12px] font-semibold" style={{ color: '#1B2A4A' }}>
                    {doc.filename}
                  </p>
                  <p className="text-[10.5px]" style={{ color: '#6B7280' }}>
                    {doc.description}
                  </p>
                </div>
                {link ? (
                  <button onClick={() => copy(link.url)}
                    className="px-2 py-1 rounded text-[11px] font-medium text-white inline-flex items-center gap-1 hover:opacity-90 flex-shrink-0"
                    style={{ backgroundColor: '#2A7B88' }}
                    data-testid={`shareable-doc-copy-${doc.doc_id}`}>
                    <Copy size={11} /> Copy link
                  </button>
                ) : (
                  <button onClick={() => issue(doc.doc_id)}
                    disabled={issuing === doc.doc_id || !doc.exists}
                    title={doc.exists ? 'Mint a fresh 30-day signed URL' : 'File missing on server'}
                    className="px-2 py-1 rounded text-[11px] font-medium text-white inline-flex items-center gap-1 hover:opacity-90 disabled:opacity-60 flex-shrink-0"
                    style={{ backgroundColor: '#1B2A4A' }}
                    data-testid={`shareable-doc-issue-${doc.doc_id}`}>
                    {issuing === doc.doc_id
                      ? <RefreshCw size={11} className="animate-spin" />
                      : <LinkIcon size={11} />}
                    {issuing === doc.doc_id ? 'Minting…' : 'Get link'}
                  </button>
                )}
              </div>
              {link && (
                <div className="mt-2">
                  <code className="block px-2 py-1.5 rounded text-[10px] font-mono break-all"
                    style={{ background: '#F4F2EE', color: '#1B2A4A' }}
                    data-testid={`shareable-doc-url-${doc.doc_id}`}>
                    {link.url}
                  </code>
                  <p className="text-[9.5px] mt-1" style={{ color: '#6B7280' }}>
                    Expires {new Date(link.expires_at).toLocaleString('en-IN',
                      { day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit' })}.
                    Anyone with this link can download the file.
                  </p>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
